if (!customElements.get('dantes-infinite-scroll')) {
  customElements.define('dantes-infinite-scroll', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.grid = this.querySelector('product-list');
      this.controls = this.querySelector('[data-infinite-controls]');
      this.next = this.querySelector('[data-infinite-next]');
      this.status = this.querySelector('[data-infinite-status]');
      this.fallback = this.querySelector('[data-infinite-fallback]');
      this.loading = false;
      this.failed = false;
      this.paused = false;
      this.visited = new Set();
      if (!this.grid || !this.controls || !this.fallback) return;
      if (!this.next) {
        const empty = this.querySelector('[data-infinite-empty]');
        if (empty) empty.hidden = !!this.grid.querySelector('product-card');
        return;
      }
      this.controls.hidden = false;
      this.fallback.hidden = true;
      this.addEventListener('click', event => {
        if (event.target.closest('[data-infinite-footer]')) {
          this.observer?.disconnect();
          this.paused = true;
          return;
        }
        const link = event.target.closest('[data-infinite-next]');
        if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || this.failed) return;
        event.preventDefault();
        this.loadMore(true);
      }, {signal: this.controller.signal});
      document.addEventListener('facet:update', event => {
        if (event.detail.url.searchParams.get('section_id') === this.getAttribute('section-id')) {
          this.controller.abort();
          this.observer?.disconnect();
        }
      }, {signal: this.controller.signal});
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting) && !this.failed && !this.paused) this.loadMore(false);
        }, {rootMargin: '0px 0px 400px 0px'});
        this.observer.observe(this.next);
      }
      if (!this.grid.querySelector('product-card')) this.loadMore(false);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }

    async loadMore(manual) {
      if (this.loading || this.failed || !this.next || this.controller.signal.aborted) return;
      this.loading = true;
      this.observer?.unobserve(this.next);
      this.grid.setAttribute('aria-busy', 'true');
      this.next.setAttribute('aria-disabled', 'true');
      this.status.textContent = this.dataset.loading;
      const seen = new Set([...this.grid.querySelectorAll('product-card')].map(card => card.getAttribute('handle')));
      let added = 0;
      let firstAdded;
      try {
        for (let attempt = 0; attempt < 10 && this.next && !added; attempt++) {
          const target = new URL(this.next.href, location.href);
          if (target.origin !== location.origin || target.pathname !== location.pathname) throw new Error('Invalid collection URL');
          target.searchParams.set('section_id', this.getAttribute('section-id'));
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) target.searchParams.set('preview_theme_id', preview);
          if (this.visited.has(target.href)) throw new Error('Repeated collection page');
          const response = await fetch(target.href, {signal: this.controller.signal, credentials: 'same-origin'});
          if (!response.ok) throw new Error('Collection request failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const incoming = [...doc.querySelectorAll('dantes-infinite-scroll')].find(element => element.getAttribute('section-id') === this.getAttribute('section-id'));
          const incomingGrid = incoming?.querySelector('product-list');
          if (!incomingGrid) throw new Error('Collection content missing');
          const incomingNext = incoming.querySelector('[data-infinite-next]');
          if (incomingNext && new URL(incomingNext.getAttribute('href'), location.href).searchParams.get('page') === target.searchParams.get('page')) throw new Error('Repeated next page');
          if (!this.isConnected || this.controller.signal.aborted) return;
          this.visited.add(target.href);
          const fragment = document.createDocumentFragment();
          for (const child of [...incomingGrid.children]) {
            if (child.matches('product-card')) {
              const handle = child.getAttribute('handle');
              if (seen.has(handle)) continue;
              seen.add(handle);
            } else {
              continue;
            }
            const card = document.importNode(child, true);
            card.removeAttribute('reveal-on-scroll');
            card.querySelectorAll('img').forEach(img => {
              img.loading = 'lazy';
              img.removeAttribute('fetchpriority');
            });
            firstAdded ||= card;
            fragment.append(card);
            added++;
          }
          this.grid.append(fragment);
          const incomingFallback = incoming.querySelector('[data-infinite-fallback]');
          if (incomingFallback) this.fallback.replaceChildren(...document.importNode(incomingFallback, true).childNodes);
          if (incomingNext) {
            this.next.href = incomingNext.getAttribute('href');
          } else {
            this.next.hidden = true;
            this.next = null;
            this.observer?.disconnect();
          }
        }
        const empty = this.querySelector('[data-infinite-empty]');
        if (empty) empty.hidden = !!this.grid.querySelector('product-card') || !!this.next;
        this.status.textContent = this.next ? (added ? this.dataset.loaded : this.dataset.more) : this.dataset.finished;
        if (!added && this.next) this.paused = true;
        if (manual && firstAdded) {
          const firstLink = firstAdded.querySelector('a[href]');
          firstLink?.focus({preventScroll: true});
          firstAdded.scrollIntoView({block: 'nearest', behavior: 'instant'});
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.failed = true;
          this.status.textContent = this.dataset.error;
          this.observer?.disconnect();
          this.fallback.hidden = false;
        }
      } finally {
        this.loading = false;
        this.grid.removeAttribute('aria-busy');
        this.next?.removeAttribute('aria-disabled');
        if (this.next && !this.failed && !this.paused && !this.controller.signal.aborted) this.observer?.observe(this.next);
      }
    }
  });
}
